import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Plus, Pencil, UserCheck, UserX, X, Check } from 'lucide-react';

const roleLabel = {
  ADMIN: 'Administrador',
  DIRECTOR: 'Director',
  GERENTE: 'Gerente',
  SUPERVISOR: 'Supervisor',
  OPERADOR: 'Operador'
};

const roleColor = {
  ADMIN:     { bg: '#fef3c7', text: '#92400e' },
  DIRECTOR:  { bg: '#ede9fe', text: '#5b21b6' },
  GERENTE:   { bg: '#dbeafe', text: '#1e40af' },
  SUPERVISOR:{ bg: '#d1fae5', text: '#065f46' },
  OPERADOR:  { bg: '#f1f5f9', text: '#475569' },
};

const emptyForm = { name: '', email: '', password: '', role: 'OPERADOR', areaId: '' };

export default function UsuariosPage() {
  const { user: currentUser } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [showAreaForm, setShowAreaForm] = useState(false);
  const [newAreaName, setNewAreaName] = useState('');
  const [editingArea, setEditingArea] = useState(null);

  const fetchAll = async () => {
    const [usersRes, areasRes] = await Promise.all([
      api.get('/auth/users'),
      api.get('/areas')
    ]);
    setUsuarios(usersRes.data);
    setAreas(areasRes.data);
  };

  useEffect(() => {
    fetchAll().finally(() => setLoading(false));
  }, []);

  const handleOpenCreate = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const handleOpenEdit = (u) => {
    setEditingUser(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role, areaId: u.areaId || '' });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, areaId: form.areaId ? Number(form.areaId) : null };
      if (!payload.password) delete payload.password;

      if (editingUser) {
        await api.patch(`/auth/users/${editingUser.id}`, payload);
      } else {
        await api.post('/auth/users', payload);
      }
      setShowForm(false);
      setEditingUser(null);
      setForm(emptyForm);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar usuario');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/auth/users/${u.id}`, { active: !u.active });
      fetchAll();
    } catch {
      alert('Error al actualizar usuario');
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`¿Eliminar permanentemente al usuario "${u.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'No se puede eliminar este usuario');
    }
  };

  const handleCreateArea = async (e) => {
    e.preventDefault();
    if (!newAreaName.trim()) return;
    try {
      await api.post('/areas', { name: newAreaName.trim() });
      setNewAreaName('');
      setShowAreaForm(false);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al crear área');
    }
  };

  const handleEditArea = async (area) => {
    const newName = prompt('Nuevo nombre del área:', area.name);
    if (!newName || newName.trim() === area.name) return;
    try {
      await api.patch(`/areas/${area.id}`, { name: newName.trim() });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al editar área');
    }
  };

  const handleDeleteArea = async (area) => {
    if (!window.confirm(`¿Eliminar el área "${area.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/areas/${area.id}`);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'No se puede eliminar esta área');
    }
  };

  const rolesDisponibles = () => {
    if (currentUser?.role === 'ADMIN') return ['ADMIN', 'DIRECTOR', 'GERENTE', 'SUPERVISOR', 'OPERADOR'];
    return ['DIRECTOR', 'GERENTE', 'SUPERVISOR', 'OPERADOR'];
  };

  if (loading) return <p style={{ color: '#64748b' }}>Cargando...</p>;

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Modal formulario usuario */}
      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200
        }}>
          <div style={{ background: 'white', borderRadius: 20, padding: 32, width: '100%', maxWidth: 480, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1a2f6b' }}>
                {editingUser ? 'Editar usuario' : 'Nuevo usuario'}
              </h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                <X size={22} />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { label: 'Nombre completo', key: 'name', type: 'text', required: true },
                { label: 'Correo electrónico', key: 'email', type: 'email', required: true },
                { label: editingUser ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña', key: 'password', type: 'password', required: !editingUser },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>{f.label}</label>
                  <input
                    type={f.type}
                    value={form[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    required={f.required}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      border: '1.5px solid #e2e8f0', borderRadius: 10,
                      padding: '10px 14px', fontSize: 14, fontFamily: 'inherit', outline: 'none'
                    }}
                    onFocus={e => e.target.style.borderColor = '#2563eb'}
                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
              ))}

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Rol</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }}>
                  {rolesDisponibles().map(r => <option key={r} value={r}>{roleLabel[r]}</option>)}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Área</label>
                <select value={form.areaId} onChange={e => setForm({ ...form, areaId: e.target.value })}
                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }}>
                  <option value="">Sin área específica</option>
                  {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button type="button" onClick={() => setShowForm(false)}
                  style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: 'white', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', color: '#64748b' }}>
                  Cancelar
                </button>
                <button type="submit" disabled={submitting}
                  style={{ flex: 1, padding: '11px', border: 'none', borderRadius: 10, background: '#2563eb', color: 'white', fontSize: 14, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                  {submitting ? 'Guardando...' : editingUser ? 'Guardar cambios' : 'Crear usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1a2f6b', margin: 0 }}>Usuarios y Áreas</h1>
        <p style={{ color: '#94a3b8', fontSize: 14, marginTop: 4 }}>Gestión de accesos y estructura organizacional</p>
      </div>

      {/* Sección usuarios */}
      <div style={{ background: 'white', borderRadius: 20, boxShadow: '0 2px 16px rgba(26,47,107,0.07)', border: '1px solid #e2e8f0', marginBottom: 24 }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a2f6b' }}>Usuarios</h2>
          <button onClick={handleOpenCreate} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: '#2563eb', color: 'white', border: 'none',
            borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
          }}>
            <Plus size={16} /> Nuevo usuario
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Nombre', 'Correo', 'Rol', 'Área', 'Estatus', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '12px 20px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f1f5f9' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usuarios.map(u => {
                const rc = roleColor[u.role] || roleColor.OPERADOR;
                return (
                  <tr key={u.id} style={{ borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                  >
                    <td style={{ padding: '13px 20px', fontWeight: 600, color: '#1e293b' }}>{u.name}</td>
                    <td style={{ padding: '13px 20px', color: '#64748b' }}>{u.email}</td>
                    <td style={{ padding: '13px 20px' }}>
                      <span style={{ background: rc.bg, color: rc.text, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                        {roleLabel[u.role]}
                      </span>
                    </td>
                    <td style={{ padding: '13px 20px', color: '#64748b' }}>{u.area?.name || '—'}</td>
                    <td style={{ padding: '13px 20px' }}>
                      <span style={{
                        background: u.active ? '#f0fdf4' : '#fef2f2',
                        color: u.active ? '#16a34a' : '#dc2626',
                        padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        border: `1px solid ${u.active ? '#bbf7d0' : '#fecaca'}`
                      }}>
                        {u.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td style={{ padding: '13px 20px' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => handleOpenEdit(u)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                          <Pencil size={13} /> Editar
                        </button>
                        <button onClick={() => toggleActive(u)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, background: u.active ? '#fef2f2' : '#f0fdf4', color: u.active ? '#dc2626' : '#16a34a', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                          {u.active ? <><UserX size={13} /> Desactivar</> : <><UserCheck size={13} /> Activar</>}
                        </button>
                        {['ADMIN', 'DIRECTOR'].includes(currentUser?.role) && (
                        <button onClick={() => deleteUser(u)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                          🗑️ Eliminar
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sección áreas — solo Admin */}
      {currentUser?.role === 'ADMIN' && (
        <div style={{ background: 'white', borderRadius: 20, boxShadow: '0 2px 16px rgba(26,47,107,0.07)', border: '1px solid #e2e8f0' }}>
          <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a2f6b' }}>Áreas</h2>
            <button onClick={() => setShowAreaForm(!showAreaForm)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#16a34a', color: 'white', border: 'none',
              borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
            }}>
              <Plus size={16} /> Nueva área
            </button>
          </div>

          {showAreaForm && (
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
              <form onSubmit={handleCreateArea} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input
                  type="text"
                  value={newAreaName}
                  onChange={e => setNewAreaName(e.target.value)}
                  placeholder="Nombre del área"
                  required
                  style={{ flex: 1, border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '9px 14px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }}
                  onFocus={e => e.target.style.borderColor = '#16a34a'}
                  onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                />
                <button type="submit" style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#16a34a', color: 'white', border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <Check size={15} /> Guardar
                </button>
                <button type="button" onClick={() => setShowAreaForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                  <X size={20} />
                </button>
              </form>
            </div>
          )}

          <div style={{ padding: '8px 0' }}>
            {areas.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', borderBottom: '1px solid #f8fafc' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >
                <span style={{ fontWeight: 500, color: '#1e293b', fontSize: 14 }}>🏢 {a.name}</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => handleEditArea(a)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    <Pencil size={12} /> Editar
                  </button>
                  <button onClick={() => handleDeleteArea(a)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    <X size={12} /> Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
